/**
 * CSS Format Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type PrettierModule = {
  format: (source: string, options: Record<string, unknown>) => string;
};

function unwrapDefault<T>(mod: unknown): T {
  const m = mod as Record<string, unknown> | null | undefined;
  return (m?.default ?? mod) as T;
}

export const CssFormatTool: ITransformerTool = {
  id: 'css-format',
  name: 'CSS Format',
  description: 'Format CSS for readability',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['css', 'format', 'beautify', 'prettify'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'printWidth',
      type: 'number',
      label: 'Print Width',
      defaultValue: 100,
      description: 'Preferred line length before wrapping',
      validation: { min: 40, max: 300, step: 10 },
    },
    {
      name: 'tabWidth',
      type: 'number',
      label: 'Tab Width',
      defaultValue: 2,
      description: 'Spaces per indentation level',
      validation: { min: 1, max: 8, step: 1 },
    },
    {
      name: 'useTabs',
      type: 'boolean',
      label: 'Use Tabs',
      defaultValue: false,
      description: 'Indent with tabs instead of spaces',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const printWidth = typeof config.printWidth === 'number' ? (config.printWidth as number) : 100;
    const tabWidth = typeof config.tabWidth === 'number' ? (config.tabWidth as number) : 2;
    const useTabs = config.useTabs === true;

    try {
      const prettierMod = await import('prettier/standalone');
      const pluginMod = await import('prettier/plugins/postcss');

      const prettier = unwrapDefault<PrettierModule>(prettierMod);
      const plugin = unwrapDefault<unknown>(pluginMod); // Dynamic import interop

      return prettier.format(String(input ?? ''), {
        parser: 'css',
        plugins: [plugin],
        printWidth,
        tabWidth,
        useTabs,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format CSS';
      throw ToolError.execution(message);
    }
  },
};
